import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  KpiAgenda,
  KpiEmployees,
  KpiOrdersDelivered,
  KpiOrdersPending,
  ShoppingVolume,
  WorkshopIncentiveTravelData,
  TravelMaterial,
  RequirementProgress,
  PointSystemRule,
} from '../interfaces/home.model';

import { ContentManagementService } from '../../admin-content-management/services/content-management.service';
import { News as ApiNews } from '../../admin-content-management/models/news.model';
import { IncentiveTravelCurrentResponse } from '../../../models/incentive-travel.model';

type FileType = 'video' | 'document' | 'image' | 'presentation';

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  private readonly baseUrl = environment.cafler_backoffice_back_url;

  constructor(
    private readonly http: HttpClient,
    private readonly contentService: ContentManagementService
  ) { }

  /**
   * No tenemos contrato del endpoint /fleet-manager/services/list en el contexto.
   * No inventamos tipos: devolvemos un array de objetos JSON genéricos.
   */
  servicesList(): Observable<ReadonlyArray<Record<string, unknown>>> {
    return this.authorizedGet<ReadonlyArray<Record<string, unknown>>>('/fleet-manager/services/list');
  }

  getShowGenericLanding(): Observable<boolean> {
    return this.authorizedGet<boolean>('/users/show-generic-landing').pipe(catchError(() => of(true)));
  }

  getKpiAgendaToday(): Observable<KpiAgenda> {
    return this.authorizedGet<KpiAgenda>('/Agenda/today-summary');
  }

  getKpiEmployeesToday(): Observable<KpiEmployees> {
    return this.authorizedGet<KpiEmployees>('/Employees/resources-summary');
  }

  getKpiOrdersDeliveredToday(): Observable<KpiOrdersDelivered> {
    return this.authorizedGet<KpiOrdersDelivered>('/Orders/today-delivered-summary');
  }

  getKpiOrdersPendingToday(): Observable<KpiOrdersPending> {
    return this.authorizedGet<KpiOrdersPending>('/Orders/today-pending-summary');
  }

  getNews(): Observable<ApiNews[]> {
    return this.contentService.getNews(true).pipe(catchError(() => of([])));
  }

  getPlatformUse(): Observable<{ usagePercent: number; goalPercent: number }> {
    return of({ usagePercent: 60, goalPercent: 100 });
  }

  getWorkshopIncentiveTravelData(): Observable<WorkshopIncentiveTravelData | null> {
    return this.authorizedGet<IncentiveTravelCurrentResponse>('/incentiveTravel/current').pipe(
      map((response) => this.mapIncentiveTravelResponse(response)),
      catchError(() => of(null))
    );
  }

  getShoppingVolume(
    startDate: Date,
    endDate: Date,
    categoryName: string = '',
    centerId: number = 0
  ): Observable<ShoppingVolume[]> {
    const params = this.buildShoppingVolumeParams(
      startDate,
      endDate,
      categoryName,
      centerId
    );

    return this.authorizedGet<ShoppingVolume[]>('/Orders/volume', params);
  }

  private authorizedGet<T>(path: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}${path}`, { params });
  }

  private buildShoppingVolumeParams(
    startDate: Date,
    endDate: Date,
    categoryName: string,
    centerId: number
  ): HttpParams {
    let params = new HttpParams()
      .set('CategoryName', categoryName)
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    if (centerId > 0) {
      params = params.set('centerId', String(centerId));
    }

    return params;
  }

  private mapIncentiveTravelResponse(
    response: IncentiveTravelCurrentResponse
  ): WorkshopIncentiveTravelData {
    const requirements: RequirementProgress[] = response.requirementsDto.map(
      (req) => {
        const currentQuantity = 0;
        const progressPercentage =
          req.minimumQuantity > 0
            ? Math.min((currentQuantity / req.minimumQuantity) * 100, 100)
            : 0;

        return {
          requirementId: req.requirementId,
          brand: req.brand,
          providerName: req.providerName,
          currentQuantity,
          requiredQuantity: req.minimumQuantity,
          progressPercentage,
          isCompleted: currentQuantity >= req.minimumQuantity,
        };
      }
    );

    const pointSystemRules: PointSystemRule[] = response.pointsSystemTyresDto.map(
      (point) => ({
        id: point.pointsSystemTyresId,
        brand: point.brand,
        acceptedMeasures: point.acceptedMeasures,
        minimumQuantity: point.minimumQuantity,
        points: point.points,
        rappelPlan: point.providerName,
      })
    );

    const materials: TravelMaterial[] = response.filesDto.map((file) => ({
      id: file.id,
      fileName: file.fileName,
      fileType: this.mapFileTypeFromNumber(file.fileType),
      fileUrl: file.fileUrl,
      fileBase64: file.fileBase64,
      description: file.description,
      mimeType: file.mimeType,
    }));

    const progressPercentage =
      response.objectivePoints > 0
        ? Math.min((response.obtainedPoints / response.objectivePoints) * 100, 100)
        : 0;

    const pointsToGoal = Math.max(response.objectivePoints - response.obtainedPoints, 0);

    return {
      travelId: this.uuidToStableInt(response.id),
      title: response.title,
      description: response.description,
      destination: response.destination,
      startDate: new Date(response.startDate),
      endDate: new Date(response.endDate),
      isActive: response.isActive,
      currentPoints: response.obtainedPoints,
      objectivePoints: response.objectivePoints,
      progressPercentage,
      pointsToGoal,
      requirements,
      pointSystemRules,
      materials,
      generalConditions: response.generalConditions,
      seasonName: `Temporada ${new Date(response.startDate).getFullYear()}`,
      isCurrentSeason: response.isActive,
    };
  }

  private mapFileTypeFromNumber(fileType: number): FileType {
    switch (fileType) {
      case 0:
        return 'document';
      case 1:
        return 'image';
      case 2:
        return 'video';
      case 3:
        return 'presentation';
      default:
        return 'document';
    }
  }

  private uuidToStableInt(uuid: string): number {
    const hex = uuid.replace(/-/g, '').slice(0, 8);
    const value = Number.parseInt(hex, 16);
    return Number.isFinite(value) ? value : 0;
  }


}
